import { useNavigate } from 'react-router-dom'
import { Button } from '../ui/Button'
import { Badge } from '../ui/Badge'
import { POSSettingsDropdown } from './POSSettingsDropdown'
import { RecentOrdersDropdown } from './RecentOrdersDropdown'
import {
  Maximize,
  Minimize,
  Pause,
  Clock,
  User,
  BarChart3,
  ArrowLeft
} from 'lucide-react'

interface POSTopHeaderProps {
  posLayout: 'cards' | 'table'
  setPosLayout: (layout: 'cards' | 'table') => void
  isFullscreen: boolean
  toggleFullscreen: () => void
  hasActiveCart: boolean
  suspendedSalesCount: number
  onSuspendSale: () => void
  onShowSuspendedSales: () => void
  recentOrders: any[] // API sales data structure
  onReprintReceipt: (orderId: string) => void
  onViewOrder: (orderId: string) => void
  formatCurrency: (amount: number) => string
  onSettings?: () => void
  onReports?: () => void
  onUserManagement?: () => void
}

export const POSTopHeader = ({
  posLayout,
  setPosLayout,
  isFullscreen,
  toggleFullscreen,
  hasActiveCart,
  suspendedSalesCount,
  onSuspendSale,
  onShowSuspendedSales,
  recentOrders,
  onReprintReceipt,
  onViewOrder,
  formatCurrency,
  onSettings,
  onReports,
  onUserManagement
}: POSTopHeaderProps) => {
  const navigate = useNavigate()
  
  const handleBackToDashboard = () => {
    navigate('/')
  }

  return (
    <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-2 sm:px-6 py-2 sm:py-4">
      {/* Mobile Layout - 2-row design */}
      <div className="flex flex-col gap-2 lg:hidden">
        {/* Row 1 - Back button and Settings */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={handleBackToDashboard}
            className="h-8 w-8 p-0 flex-shrink-0"
            title="Back to Dashboard"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>

          <h1 className="text-lg font-bold text-gray-800 dark:text-gray-100">
            POS
          </h1>

          <div className="flex-shrink-0">
            <POSSettingsDropdown
              posLayout={posLayout}
              setPosLayout={setPosLayout}
              onGeneralSettings={onSettings}
            />
          </div>
        </div>

        {/* Row 2 - Action buttons */}
        <div className="flex items-center justify-center gap-2 overflow-x-auto pb-1">
          <Button
            variant="outline"
            size="sm"
            onClick={onShowSuspendedSales}
            className="h-8 w-8 p-0 flex-shrink-0 relative"
            title="View Suspended Sales"
          >
            <Clock className="h-3 w-3" />
            {suspendedSalesCount > 0 && (
              <Badge 
                variant="destructive" 
                className="absolute -top-1 -right-1 h-4 w-4 p-0 text-xs flex items-center justify-center"
              >
                {suspendedSalesCount}
              </Badge>
            )}
          </Button>
          
          {hasActiveCart && (
            <Button
              variant="outline"
              size="sm"
              onClick={onSuspendSale}
              className="h-8 w-8 p-0 flex-shrink-0 text-orange-600 hover:text-orange-700"
              title="Suspend Current Sale"
            >
              <Pause className="h-3 w-3" />
            </Button>
          )}
          
          <div className="flex-shrink-0">
            <RecentOrdersDropdown
              recentOrders={recentOrders}
              onReprintReceipt={onReprintReceipt}
              onViewOrder={onViewOrder}
              formatCurrency={formatCurrency}
              compact={true}
            />
          </div>
          
          {onReports && (
            <Button
              variant="outline"
              size="sm"
              onClick={onReports}
              className="h-8 w-8 p-0 flex-shrink-0"
              title="Reports & Analytics"
            >
              <BarChart3 className="h-3 w-3" />
            </Button>
          )}
          
          {onUserManagement && (
            <Button
              variant="outline"
              size="sm"
              onClick={onUserManagement}
              className="h-8 w-8 p-0 flex-shrink-0"
              title="User Management"
            >
              <User className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>

      {/* Desktop Layout - Horizontal */}
      <div className="hidden lg:flex items-center justify-between">
        {/* Left Section - Back button and Title */}
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleBackToDashboard}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Dashboard</span>
          </Button>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
            Point of Sale
          </h1>
        </div>

        {/* Right Section - Controls */}
        <div className="flex items-center gap-3">
          {/* Sales Management */}
          <div className="flex items-center gap-2 px-3 py-1 bg-gray-50 dark:bg-gray-700 rounded-lg">
            {/* Suspended Sales */}
            <Button
              variant="ghost"
              size="sm"
              onClick={onShowSuspendedSales}
              className="flex items-center gap-2 h-8 text-xs relative"
            >
              <Clock className="h-4 w-4" />
              Suspended
              {suspendedSalesCount > 0 && (
                <Badge 
                  variant="destructive" 
                  className="absolute -top-1 -right-1 h-4 w-4 p-0 text-xs flex items-center justify-center"
                >
                  {suspendedSalesCount}
                </Badge>
              )}
            </Button>

            {/* Suspend Current Sale */}
            {hasActiveCart && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onSuspendSale}
                className="flex items-center gap-2 h-8 text-xs text-orange-600 hover:text-orange-700"
              >
                <Pause className="h-4 w-4" />
                Suspend
              </Button>
            )}
          </div>

          {/* Recent Orders */}
          <RecentOrdersDropdown
            recentOrders={recentOrders}
            onReprintReceipt={onReprintReceipt}
            onViewOrder={onViewOrder}
            formatCurrency={formatCurrency}
          />

          {/* System Controls */}
          <div className="flex items-center gap-2">
            {/* Reports */}
            {onReports && (
              <Button
                variant="outline"
                size="sm"
                onClick={onReports}
                className="flex items-center gap-2 h-8 text-xs"
                title="Reports & Analytics"
              >
                <BarChart3 className="h-4 w-4" />
                Reports
              </Button>
            )}

            {/* User Management */}
            {onUserManagement && (
              <Button
                variant="outline"
                size="sm"
                onClick={onUserManagement}
                className="flex items-center gap-2 h-8 text-xs"
                title="User Management"
              >
                <User className="h-4 w-4" />
                Users
              </Button>
            )}

            {/* Settings Dropdown (includes layout toggle) */}
            <POSSettingsDropdown
              posLayout={posLayout}
              setPosLayout={setPosLayout}
              onGeneralSettings={onSettings}
            />
            
            {/* Fullscreen Toggle */}
            <Button
              variant="outline"
              size="sm"
              onClick={toggleFullscreen}
              className="flex items-center gap-2 h-8 text-xs"
            >
              {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
              {isFullscreen ? 'Exit' : 'Full'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}